import type { CSSProperties, ReactNode } from 'react';
import type { QuestionBeta, Session } from '../api/contrat.js';
import { BandeauVoirEnTantQue } from './BandeauVoirEnTantQue.js';
import { BarreOngletsDossier, RenvoiListeDossiers } from './barre-onglets-dossier.js';
import { HoteBeta } from './beta.js';
import { ecranParChemin } from './ecrans.js';
import { IndicateurHorsLigne } from './hors-ligne.js';
import { EnteteMaat, FeuilleTheme, styleBoutonEntete, styleLienEntete } from './maat.js';
import { Lien } from './navigation.js';
import { dossierDeLAdresse, ECRANS_AVEC_ONGLETS, ECRANS_DU_DOSSIER, ongletsDossier } from './onglets-dossier.js';
import { BoutonInstaller, type EtatInstallation } from './pwa.js';
import { roleEffectif, type Layout } from './roles.js';
import { elementsNavigation } from './routeur.js';
import { CIBLE_MIN, COULEURS, POLICE_EQUIPE, POLICE_SYSTEME } from './styles.js';

/**
 * Les quatre layouts de la coquille : équipe sur bureau (bandeau marine, navigation horizontale), équipe sur mobile (bandeau avec menu replié),
 * externes (clerc, notaire) et portail (mandant, héritier). Chacun porte les services transverses : bandeau
 * « voir en tant que », indicateur de réseau, installation, hôte des pop-ups BETA.
 */
export interface ServicesCoquille {
  enLigne: boolean;
  enAttente: number;
  installation: EtatInstallation;
  questionsBeta: readonly QuestionBeta[];
  reporteesBeta: readonly string[];
  saisieEnCours: boolean;
  appelEnCours: boolean;
  onInstaller: () => void;
  onRevenir: () => void;
  onDeconnexion: () => void;
  onRepondreBeta: (question: QuestionBeta, option: string) => void;
  onPlusTardBeta: (question: QuestionBeta) => void;
}

export interface PropsCoquille {
  layout: Layout;
  session: Session;
  chemin: string;
  largeur: number;
  services: ServicesCoquille;
  children: ReactNode;
}

const cheminSeul = (chemin: string): string => (chemin.split(/[?#]/, 1)[0] ?? '/').replace(/(.)\/+$/, '$1');

/** Portail et externes s'adressent à des clients : 18 px au moins partout (UX-20). */
const estClient = (layout: Layout): boolean => layout === 'portail' || layout === 'externe';

function stylePourLayout(layout: Layout): CSSProperties {
  const client = layout === 'portail';
  return {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: client ? POLICE_SYSTEME : POLICE_EQUIPE,
    fontSize: client || layout === 'externe' ? 18 : 16,
    lineHeight: 1.5,
    color: COULEURS.marineFonce,
    background: client ? COULEURS.blanc : COULEURS.surface,
  };
}

export function Coquille({ layout, session, chemin, largeur, services, children }: PropsCoquille) {
  const client = estClient(layout);
  const equipe = !client;
  const role = roleEffectif(session);
  // Les écrans mobiles (M…) de l'équipe ne figurent pas dans le bandeau du bureau : ils y tiendraient sur trois lignes.
  const elements = elementsNavigation(role, largeur).filter((e) => layout !== 'equipe-bureau' || !e.id.startsWith('M'));
  const actuel = cheminSeul(chemin);
  const utilisateur = session.effectiveUser ?? session.user;
  // Onglets du dossier ouvert (?dossier=) sur les écrans du dossier ; D03 porte les siens dans sa vue d'ensemble.
  const idDossier = equipe ? dossierDeLAdresse(chemin) : null;
  const ecranCourant = ecranParChemin(actuel);
  const onglets = idDossier && ecranCourant && ECRANS_AVEC_ONGLETS.includes(ecranCourant.id) ? ongletsDossier(role, idDossier) : [];
  const sansDossier = equipe && ecranCourant !== null && ECRANS_DU_DOSSIER.includes(ecranCourant.id) && dossierDeLAdresse(chemin) === null;
  const ongletCourant = ecranCourant?.id === 'D04b' ? 'D04' : (ecranCourant?.id ?? null);
  const liens = elements.map((e) => (
    <Lien key={e.id} vers={e.chemin} nav={e.id} courant={e.chemin === actuel} style={styleLienEntete(e.chemin === actuel)} enfants={e.libelle} />
  ));
  const actions = (
    <>
      <BoutonInstaller etat={services.installation} onInstaller={services.onInstaller} />
      <button type="button" onClick={services.onDeconnexion} style={styleBoutonEntete}>
        Se déconnecter
      </button>
    </>
  );
  return (
    <div data-layout={layout} style={stylePourLayout(layout)}>
      <FeuilleTheme equipe={equipe} />
      <a
        href="#contenu"
        data-lien-evitement=""
        style={{ display: 'inline-flex', alignItems: 'center', minHeight: CIBLE_MIN, padding: '0 16px', color: COULEURS.marine }}
      >
        Aller au contenu
      </a>
      <BandeauVoirEnTantQue session={session} onRevenir={services.onRevenir} />
      <IndicateurHorsLigne enLigne={services.enLigne} enAttente={services.enAttente} client={client} />
      <EnteteMaat mobile={layout === 'equipe-mobile'} nom={utilisateur.displayName} liens={liens} actions={actions} />
      <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
        <main
          id="contenu"
          style={{
            flex: '1 1 0',
            minWidth: 0,
            width: '100%',
            boxSizing: 'border-box',
            margin: '0 auto',
            padding: layout === 'portail' ? '24px 16px' : layout === 'equipe-mobile' ? 16 : '24px max(16px, 24px) 48px',
            maxWidth: layout === 'portail' ? 680 : equipe ? 1320 : undefined,
          }}
        >
          {sansDossier ? <RenvoiListeDossiers liste="/dossier" nouveau="/dossier?nouveau=1" /> : null}
          {onglets.length > 0 ? <BarreOngletsDossier onglets={onglets} courant={ongletCourant} defilant={layout === 'equipe-mobile'} /> : null}
          {children}
        </main>
      </div>
      <footer style={{ padding: '12px 16px', color: COULEURS.ardoise, fontSize: client ? 18 : 14 }}>Cabinet MAAT</footer>
      <HoteBeta
        role={role}
        questions={services.questionsBeta}
        reportees={services.reporteesBeta}
        saisieEnCours={services.saisieEnCours}
        appelEnCours={services.appelEnCours}
        onRepondre={services.onRepondreBeta}
        onPlusTard={services.onPlusTardBeta}
      />
    </div>
  );
}
